import React from 'react';
import { useSelector } from 'react-redux';
import { timeUntil } from 'helpers/PitchLimitHelper';
import {
  APPLICATION_CREDITS_BUNDLE_SIZE,
  LOW_CREDITS_THRESHOLD,
  EXPIRING_SOON_DAYS,
  daysUntil,
  formatCreditDate,
  getCreditPacks,
  getTotalCreditsPurchased,
} from 'helpers/ApplicationCreditsHelper';
import ApplicationCreditsBar from 'components/Shared/ApplicationCreditsBar/ApplicationCreditsBar';
import styles from './ApplicationCreditsSection.module.scss';
import PropTypes from 'prop-types';

const ApplicationCreditsSection = ({ mobile = false }) => {
  const { introsLeft, introsLeftMax, applicationCreditsRemaining } =
    useSelector((s) => s.messages);
  const user = useSelector((s) => s.auth.user);

  if (!user) return null;

  const packs = getCreditPacks(user);
  const totalCreditsPurchased = getTotalCreditsPurchased(packs);
  const creditsBalance = applicationCreditsRemaining;

  const showMonthlyBarCheck = user.pro && !user.premium && introsLeftMax > 1;
  const showCreditsBlock = user.has_purchased_credits && creditsBalance > 0;

  if (!showCreditsBlock && !showMonthlyBarCheck) return null;

  const isLowCount =
    creditsBalance > 0 && creditsBalance <= LOW_CREDITS_THRESHOLD;
  const earliestPack = packs[0];
  const earliestExpiresDays = earliestPack
    ? daysUntil(earliestPack.expires_at)
    : null;
  const expiryExpiringSoon =
    earliestExpiresDays !== null && earliestExpiresDays <= EXPIRING_SOON_DAYS;

  const showMonthlyBar = showMonthlyBarCheck;
  const today = new Date();
  const joined = new Date(user.joined);
  const nextRenewal = new Date(
    today.getFullYear(),
    today.getMonth(),
    joined.getDate()
  );
  if (nextRenewal <= today) nextRenewal.setMonth(nextRenewal.getMonth() + 1);
  const renewsIn = timeUntil(today, nextRenewal);
  const proAllowanceExhausted = introsLeft === 0;
  const proBarWidth =
    introsLeftMax > 0 ? Math.round((introsLeft / introsLeftMax) * 100) : 0;

  const creditBarWidth =
    totalCreditsPurchased > 0
      ? Math.round((creditsBalance / totalCreditsPurchased) * 100)
      : 0;

  const sectionClass = mobile ? styles.sectionMobile : styles.section;

  return (
    <div className={sectionClass}>
      {showMonthlyBar && (
        <div className={styles.block}>
          <div className={styles.labelRow}>
            <span className={styles.label}>Monthly applications</span>
            <span className={styles.count}>
              {introsLeft}/{introsLeftMax}
            </span>
          </div>
          <ApplicationCreditsBar width={proBarWidth} variant="orange" />
          <p className={styles.subtext}>
            {proAllowanceExhausted
              ? `Allowance used · resets in ${renewsIn}`
              : `${introsLeft} application${
                  introsLeft === 1 ? '' : 's'
                } remaining · resets in ${renewsIn}`}
          </p>
        </div>
      )}

      {showCreditsBlock && (
        <div className={styles.block}>
          <div className={styles.labelRow}>
            <span className={styles.label}>
              {showMonthlyBar ? 'Pay As You Go credits' : 'Application credits'}
            </span>
            <span
              className={`${styles.count} ${
                isLowCount ? styles.textDanger : ''
              }`}
            >
              {creditsBalance}/
              {totalCreditsPurchased || APPLICATION_CREDITS_BUNDLE_SIZE}
            </span>
          </div>
          <ApplicationCreditsBar
            width={creditBarWidth}
            variant={isLowCount ? 'red' : 'blue'}
          />
          {packs.length > 1 ? (
            <>
              <p className={styles.subtext}>
                Across {packs.length} credit packs
              </p>
              <table className={styles.packTable}>
                <tbody>
                  {packs.map((pack, i) => {
                    const days = daysUntil(pack.expires_at);
                    const packExpiring = days <= EXPIRING_SOON_DAYS;
                    return (
                      <tr key={i}>
                        <td className={styles.packLabel}>
                          Pack {i + 1}:{' '}
                          <strong>{pack.credits_remaining} left</strong>
                        </td>
                        <td
                          className={`${styles.packExpiry} ${
                            packExpiring ? styles.textDanger : styles.textMuted
                          }`}
                        >
                          Expires in {days} day{days === 1 ? '' : 's'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : earliestPack ? (
            <p
              className={`${styles.subtext} ${
                expiryExpiringSoon ? styles.textDanger : ''
              }`}
            >
              {isLowCount
                ? `Only ${creditsBalance} left · expires in ${earliestExpiresDays} day${
                    earliestExpiresDays === 1 ? '' : 's'
                  } (${formatCreditDate(earliestPack.expires_at)})`
                : `Expires in ${earliestExpiresDays} day${
                    earliestExpiresDays === 1 ? '' : 's'
                  } (${formatCreditDate(earliestPack.expires_at)})`}
            </p>
          ) : null}
          <a href="/pricing/creative" className={styles.cta}>
            Buy more credits →
          </a>
        </div>
      )}
    </div>
  );
};

ApplicationCreditsSection.propTypes = {
  mobile: PropTypes.bool,
};

export default ApplicationCreditsSection;
